import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useState } from 'react';

import { Input } from '@/components/ui/input';

type SearchInputProperties = {
  onChange: (value: string) => void;
  placeholder?: string;
  value: string;
};

type FilterChipProperties = {
  children: ReactNode;
  onRemove?: () => void;
  removeLabel?: string;
};

type TokenInputProperties = {
  onChange: (values: string[]) => void;
  placeholder?: string;
  values: string[];
};

export const SearchInput = ({ onChange, placeholder, value }: SearchInputProperties) => (
  <Input onChange={(event) => onChange(event.target.value)} placeholder={placeholder} type="search" value={value} />
);

export const FilterChip = ({ children, onRemove, removeLabel = 'Remove filter' }: FilterChipProperties) => (
  <span className="border-line bg-sidebar text-muted inline-flex max-w-full items-center gap-1 rounded-full border px-2 py-1 text-xs font-semibold">
    <span className="max-w-[min(28rem,70vw)] truncate">{children}</span>
    {onRemove ? (
      <button aria-label={removeLabel} className="hover:bg-selection hover:text-ink rounded-full p-0.5" onClick={onRemove} type="button">
        <X aria-hidden="true" className="size-3" />
      </button>
    ) : null}
  </span>
);

export const TokenInput = ({ onChange, placeholder, values }: TokenInputProperties) => {
  const [draft, setDraft] = useState('');

  const commit = () => {
    const nextValues = draft
      .split(',')
      .map((value) => value.trim())
      .filter((value) => value && !values.includes(value));
    if (nextValues.length > 0) {
      onChange([...values, ...nextValues]);
    }
    setDraft('');
  };

  return (
    <div className="border-line bg-raised focus-within:border-accent grid min-h-10 gap-2 rounded-md border p-2">
      {values.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {values.map((value) => (
            <FilterChip key={value} onRemove={() => onChange(values.filter((item) => item !== value))} removeLabel={`Remove ${value}`}>
              {value}
            </FilterChip>
          ))}
        </div>
      ) : null}
      <Input
        className="min-h-7 border-0 bg-transparent px-1 py-0 shadow-none focus:border-0"
        onBlur={commit}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ',') {
            event.preventDefault();
            commit();
          }
        }}
        placeholder={values.length === 0 ? placeholder : 'Add another'}
        value={draft}
      />
    </div>
  );
};

export const ActiveFilterBar = ({ children }: { children: ReactNode }) => (
  <div aria-label="Active filters" className="border-line bg-panel flex min-w-0 flex-wrap items-center gap-2 rounded-lg border px-3 py-2">
    <span className="text-muted mr-1 text-xs font-semibold">Active filters</span>
    {children}
  </div>
);
